import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from 'react'
import {
  AmenitiesContextState,
  AmenitiesFilters,
  AmenityCategory,
  AmenityPOI,
  AmenitiesData,
  AmenitiesSearchParams,
} from '@popety-io/types'
import { amenitiesService } from '../services/amenitiesService'

type AmenitiesAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_DATA'; payload: AmenitiesData }
  | { type: 'SET_FILTERS'; payload: Partial<AmenitiesFilters> }
  | { type: 'SET_SELECTED_POI'; payload: AmenityPOI | null }
  | { type: 'SET_MAP_VIEW'; payload: 'standard' | 'heatmap' | 'clusters' }
  | { type: 'TOGGLE_CATEGORY'; payload: AmenityCategory }
  | { type: 'EXPAND_ALL_CATEGORIES' }
  | { type: 'COLLAPSE_ALL_CATEGORIES' }

interface AmenitiesContextValue extends AmenitiesContextState {
  // Actions
  fetchAmenities: (location: {
    latitude: number
    longitude: number
  }) => Promise<void>
  updateFilters: (filters: Partial<AmenitiesFilters>) => void
  setSelectedPOI: (poi: AmenityPOI | null) => void
  setMapView: (view: 'standard' | 'heatmap' | 'clusters') => void
  toggleCategory: (category: AmenityCategory) => void
  expandAllCategories: () => void
  collapseAllCategories: () => void
  retryFetch: () => void
  clearError: () => void
}

const initialFilters: AmenitiesFilters = {
  search_radius: 2400, // 2.4km default
  selected_categories: [],
  show_only_open: false,
  sort_by: 'distance',
}

const initialState: AmenitiesContextState = {
  data: null,
  loading: false,
  error: null,
  filters: initialFilters,
  selected_POI: null,
  map_view: 'standard',
  expanded_categories: new Set<AmenityCategory>([
    'transport',
    'grocery',
    'education',
  ]),
}

function amenitiesReducer(
  state: AmenitiesContextState,
  action: AmenitiesAction
): AmenitiesContextState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
        error: action.payload ? null : state.error,
      }

    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }

    case 'SET_DATA':
      return { ...state, data: action.payload, loading: false, error: null }

    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      }

    case 'SET_SELECTED_POI':
      return { ...state, selected_POI: action.payload }

    case 'SET_MAP_VIEW':
      return { ...state, map_view: action.payload }

    case 'TOGGLE_CATEGORY': {
      const newExpanded = new Set(state.expanded_categories)

      if (newExpanded.has(action.payload)) {
        newExpanded.delete(action.payload)
      } else {
        newExpanded.add(action.payload)
      }

      return { ...state, expanded_categories: newExpanded }
    }

    case 'EXPAND_ALL_CATEGORIES':
      return {
        ...state,
        expanded_categories: new Set<AmenityCategory>([
          'transport',
          'grocery',
          'education',
          'dining',
          'health',
          'recreation',
          'shopping',
          'services',
          'finance',
          'culture',
        ] as AmenityCategory[]),
      }

    case 'COLLAPSE_ALL_CATEGORIES':
      return { ...state, expanded_categories: new Set() }

    default:
      return state
  }
}

const AmenitiesContext = createContext<AmenitiesContextValue | null>(null)

interface AmenitiesProviderProps {
  children: React.ReactNode
  location?: { latitude: number; longitude: number }
  defaultRadius?: number
  enabledCategories?: AmenityCategory[]
  initialData?: AmenitiesData | AmenityPOI[] | null // Accept processed data or raw POIs
}

export const AmenitiesProvider: React.FC<AmenitiesProviderProps> = ({
  children,
  location,
  defaultRadius = 2400,
  enabledCategories,
  initialData,
}) => {
  // Handle initial data - it could be already processed or raw POIs
  const processedInitialData = initialData
    ? (initialData as any).overall_score // Check if it's already processed AmenitiesData
      ? (initialData as AmenitiesData) // Use as-is if already processed
      : amenitiesService.processAmenitiesData(initialData as any, {
          categories: enabledCategories,
        })
    : null

  const [state, dispatch] = useReducer(amenitiesReducer, {
    ...initialState,
    data: processedInitialData, // Set initial data if provided
    filters: {
      ...initialFilters,
      search_radius: defaultRadius,
      selected_categories: enabledCategories || [],
    },
    // Ensure expanded_categories is always a Set
    expanded_categories: new Set<AmenityCategory>([
      'transport',
      'grocery',
      'education',
    ]),
  })

  const fetchAmenities = useCallback(
    async (searchLocation: { latitude: number; longitude: number }) => {
      dispatch({ type: 'SET_LOADING', payload: true })

      try {
        const searchParams: AmenitiesSearchParams = {
          latitude: searchLocation.latitude,
          longitude: searchLocation.longitude,
          radius: state.filters.search_radius,
          categories:
            state.filters.selected_categories.length > 0
              ? state.filters.selected_categories
              : undefined,
        }

        const response = await amenitiesService.searchPOIs(searchParams)

        const processedData = amenitiesService.processAmenitiesData(response, {
          categories: enabledCategories,
        })

        dispatch({ type: 'SET_DATA', payload: processedData })
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to fetch amenities data'
        dispatch({ type: 'SET_ERROR', payload: errorMessage })
      }
    },
    [
      state.filters.search_radius,
      state.filters.selected_categories,
      enabledCategories,
      location,
    ]
  )

  const updateFilters = useCallback((newFilters: Partial<AmenitiesFilters>) => {
    dispatch({ type: 'SET_FILTERS', payload: newFilters })
  }, [])

  const setSelectedPOI = useCallback((poi: AmenityPOI | null) => {
    dispatch({ type: 'SET_SELECTED_POI', payload: poi })
  }, [])

  const setMapView = useCallback(
    (view: 'standard' | 'heatmap' | 'clusters') => {
      dispatch({ type: 'SET_MAP_VIEW', payload: view })
    },
    []
  )

  const toggleCategory = useCallback((category: AmenityCategory) => {
    dispatch({ type: 'TOGGLE_CATEGORY', payload: category })
  }, [])

  const expandAllCategories = useCallback(() => {
    dispatch({ type: 'EXPAND_ALL_CATEGORIES' })
  }, [])

  const collapseAllCategories = useCallback(() => {
    dispatch({ type: 'COLLAPSE_ALL_CATEGORIES' })
  }, [])

  const retryFetch = useCallback(() => {
    if (location) {
      fetchAmenities(location)
    }
  }, [location, fetchAmenities])

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null })
  }, [])

  // Auto-fetch when location changes (skip if initial data was provided)
  useEffect(() => {
    if (location && !state.loading && !state.data && !processedInitialData) {
      fetchAmenities(location)
    }
  }, [location, state.loading, state.data, processedInitialData])

  // Sync with initialData when it changes (e.g., after orchestrator saves data)
  useEffect(() => {
    if (initialData && !state.data) {
      const processed = (initialData as any).overall_score
        ? (initialData as AmenitiesData)
        : amenitiesService.processAmenitiesData(initialData as any, {
            categories: enabledCategories,
          })
      dispatch({ type: 'SET_DATA', payload: processed })
    }
  }, [initialData, state.data, enabledCategories])

  // Re-fetch when filters change
  useEffect(() => {
    if (
      location &&
      state.data &&
      state.filters.search_radius !== state.data.search_radius
    ) {
      fetchAmenities(location)
    }
  }, [location, state.filters.search_radius, state.data, fetchAmenities])

  const contextValue: AmenitiesContextValue = {
    ...state,
    fetchAmenities,
    updateFilters,
    setSelectedPOI,
    setMapView,
    toggleCategory,
    expandAllCategories,
    collapseAllCategories,
    retryFetch,
    clearError,
  }

  return (
    <AmenitiesContext.Provider value={contextValue}>
      {children}
    </AmenitiesContext.Provider>
  )
}

export const useAmenities = (): AmenitiesContextValue => {
  const context = useContext(AmenitiesContext)
  if (!context) {
    throw new Error('useAmenities must be used within an AmenitiesProvider')
  }
  return context
}

export default AmenitiesContext
